import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import QRCode from "react-qr-code";
import { toBlob } from "html-to-image";
import { deleteTableQr, getTableQrs } from "../../slices/TableQrs/thunk";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { resolveApiMessage } from "../../helpers/apiMessage";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

const QR_SIZES = [
    { label: "3 CM X 5 CM", w: 3, h: 5 },
    { label: "4 CM X 6 CM", w: 4, h: 6 },
    { label: "4 CM X 7 CM", w: 4, h: 7 },
    { label: "4 CM X 8 CM", w: 4, h: 8 },
    { label: "4 CM X 9 CM", w: 4, h: 9 },
    { label: "4 CM X 10 CM", w: 4, h: 10 },
    { label: "5 CM X 6 CM", w: 5, h: 6 },
    { label: "5 CM X 7 CM", w: 5, h: 7 },
    { label: "5 CM X 8 CM", w: 5, h: 8 },
    { label: "5 CM X 9 CM", w: 5, h: 9 },
    { label: "5 CM X 10 CM", w: 5, h: 10 },
    { label: "6 CM X 7 CM", w: 6, h: 7 },
    { label: "6 CM X 8 CM", w: 6, h: 8 },
    { label: "6 CM X 9 CM", w: 6, h: 9 },
    { label: "6 CM X 10 CM", w: 6, h: 10 },
    { label: "7 CM X 8 CM", w: 7, h: 8 },
    { label: "7 CM X 9 CM", w: 7, h: 9 },
    { label: "7 CM X 10 CM", w: 7, h: 10 },
    { label: "8 CM X 9 CM", w: 8, h: 9 },
    { label: "8 CM X 10 CM", w: 8, h: 10 },
    { label: "9 CM X 10 CM", w: 9, h: 10 },
];

const ShopQrCode = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { items, loading, error } = useSelector((state: any) => state.TableQrs);

    const [deleteId, setDeleteId] = useState<string | number | null>(null);
    const [viewItem, setViewItem] = useState<any | null>(null);

    const [qrSizeOption, setQrSizeOption] = useState(QR_SIZES[0]);
    const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait");

    const qrPreviewRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        dispatch(getTableQrs());
    }, [dispatch]);

    const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    const buildQrUrl = (token?: string) => {
        const base = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
        return `${base}/web-menu/${token ?? ""}`;
    };

    const getZoneName = (item: any) => item?.zone_name ?? "Chưa có khu vực";
    const getTableLabel = (item: any) => item?.table_number ?? item?.table_id;

    const getOutputCm = () => {
        const min = Math.min(qrSizeOption.w, qrSizeOption.h);
        const max = Math.max(qrSizeOption.w, qrSizeOption.h);

        return orientation === "portrait"
            ? { widthCm: min, heightCm: max }
            : { widthCm: max, heightCm: min };
    };

    const getQrSize = () => {
        const { widthCm, heightCm } = getOutputCm();
        const shortSide = Math.min(widthCm, heightCm);

        if (shortSide <= 3) return 70;
        if (shortSide <= 4) return 95;
        if (shortSide <= 5) return 120;
        if (shortSide <= 6) return 145;

        return 170;
    };

    const handleDownloadQrImage = async () => {
        if (!viewItem || !qrPreviewRef.current) return;

        try {
            const imageBlob = await toBlob(qrPreviewRef.current, {
                backgroundColor: "#ffffff",
                cacheBust: true,
                pixelRatio: 4,
            });

            if (!imageBlob) {
                throw new Error("Không thể tạo ảnh mã QR.");
            }

            const link = document.createElement("a");
            const imageUrl = URL.createObjectURL(imageBlob);

            link.href = imageUrl;
            link.download = `qr-${getZoneName(viewItem)}-${getTableLabel(viewItem)}-${qrSizeOption.label}-${orientation}.png`;
            // iOS standalone PWAs do not reliably honor clicks on detached links.
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(imageUrl), 0);
        } catch (e) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(e)));
        }
    };

    const handleConfirmDeleteQr = async () => {
        if (!deleteId) return;

        const qrId = deleteId;
        setDeleteId(null);

        try {
            const response = await dispatch(deleteTableQr(qrId)).unwrap();
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
            dispatch(getTableQrs());
        } catch (e) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(e)));
        }
    };

    const qrSize = getQrSize();
    const { widthCm, heightCm } = getOutputCm();

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={!!deleteId}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa QR này không?"
                onConfirm={handleConfirmDeleteQr}
                onCancel={() => setDeleteId(null)}
            />

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Mã QR</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div className="filter-option">
                            <i onClick={() => navigate("/shop/qr/create")} className="mdi mdi-plus"></i>
                        </div>

                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        <div className="alert alert-info small mb-3" role="alert">
                            Nên kiểm tra định kỳ các mã QR tại từng bàn bằng tính năng {" "}
                            <Link to="/shop/qr/check" className="alert-link">
                                Kiểm tra mã QR
                            </Link>{" "}
                            để đảm bảo khách hàng luôn quét đúng mã QR hợp lệ.
                        </div>

                        {loading && <p className="text-center text-muted">Đang tải danh sách QR...</p>}

                        {!loading && list.length === 0 && (
                            <p className="text-center text-muted">{error ?? "Chưa có mã QR nào."}</p>
                        )}

                        {list.map((item: any) => (
                            <div
                                className={`cart-table card mb-3 ${item?.is_active ? "" : "border border-warning"}`}
                                key={item.id}
                            >
                                <div className="table-responsive card-body d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="mb-1">Bàn {item.table_number ?? item.table_id}</h6>
                                        <small className="text-muted">{item.zone_name ?? "Chưa có khu vực"}</small>

                                        {!item?.is_active && (
                                            <div>
                                                <span className="badge bg-warning text-dark mt-2">
                                                    Không được sử dụng
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => setViewItem(item)}
                                        >
                                            Xem QR
                                        </button>

                                        <button
                                            className="btn btn-sm btn-danger text-white"
                                            onClick={() => setDeleteId(item.id)}
                                        >
                                            Xóa
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {viewItem && (
                <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    role="dialog"
                    style={{ background: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {getZoneName(viewItem)} - Bàn {getTableLabel(viewItem)}
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setViewItem(null)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Khổ</label>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                orientation === "landscape"
                                                    ? "btn-danger"
                                                    : "btn-outline-secondary"
                                            }`}
                                            onClick={() => setOrientation("landscape")}
                                        >
                                            Khổ ngang
                                        </button>

                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                orientation === "portrait"
                                                    ? "btn-danger"
                                                    : "btn-outline-secondary"
                                            }`}
                                            onClick={() => setOrientation("portrait")}
                                        >
                                            Khổ dọc
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Kích thước</label>

                                    <div className="d-flex flex-wrap gap-2">
                                        {QR_SIZES.map((size) => (
                                            <button
                                                key={size.label}
                                                type="button"
                                                className={`btn btn-sm ${
                                                    qrSizeOption.label === size.label
                                                        ? "btn-danger"
                                                        : "btn-light"
                                                }`}
                                                onClick={() => setQrSizeOption(size)}
                                            >
                                                {size.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-center">
                                    <div
                                        ref={qrPreviewRef}
                                        className="d-inline-flex flex-column align-items-center justify-content-center bg-white border rounded text-center"
                                        style={{
                                            width: `${widthCm}cm`,
                                            height: `${heightCm}cm`,
                                            maxWidth: "100%",
                                            padding: "0.25cm",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                marginBottom: 4,
                                            }}
                                        >
                                            Quét mã để đặt món
                                        </div>

                                        <QRCode value={buildQrUrl(viewItem.qr_token)} size={qrSize} />

                                        <div
                                            style={{
                                                marginTop: 5,
                                                fontSize: 9,
                                                fontStyle: "italic",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Đặt món và gọi phục vụ
                                        </div>

                                        <div
                                            style={{
                                                marginTop: 4,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {getZoneName(viewItem)} - Bàn {getTableLabel(viewItem)}
                                        </div>
                                    </div>

                                    <div className="text-muted small mt-2">
                                        Kích thước ảnh: {widthCm}cm x {heightCm}cm
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleDownloadQrImage}
                                >
                                    Tải xuống
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setViewItem(null)}
                                >
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ShopQrCode;
